import React, { useEffect, useMemo, useReducer, useState } from 'react';

import {
    GetPaperPlanMetadataInput,
    GetPaperPlanMetadataOutput,
    PlanMetadata,
    Territories
} from '@amzn/coral_com-amazon-musicpapercontractconfigurationservice';
import { UnknownCoralError } from '@amzn/ihm-js-coral-client';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Loader from '@amzn/meridian/loader';
import Row from '@amzn/meridian/row';

import { isEmpty } from 'lodash';
import { Route, Switch, useHistory, useLocation } from 'react-router-dom';
import { contractConfigurationClient } from 'src/api/client';
import { DEFAULT_TOASTER_TIMEOUT } from 'src/constants';
import { ROUTES } from 'src/constants/routes';
import { AppContext } from 'src/contexts/appContext';
import { IBundlePricingRowData } from 'src/types/IBundlePricing';
import { IToast } from 'src/types/IToaster';
import { dropUndefined } from 'src/util/list-utils';

import { BreadcrumbWrapper } from '../Breadcrumb';
import { mapPaperPlanMetadataToBundlePricingRowData } from '../BundlePricing/BundlePricingUtils';
import { ConfirmationModal } from '../ConfirmationModal';
import { StepTracker } from '../StepTracker';
import {
    ADD_BUNDLE_PRICING_BREADCRUMB_ITEMS,
    ADD_BUNDLE_PRICING_STEPS,
    convertBundlePricingRowDataToApiPayload
} from './AddBundlePricingUtils';
import { DEFAULT_BUNDLE_PRICING_ERRORS, bundlePricingValidationReducer } from './bundlePricingValidationReducer';

export const AddBundlePricing: React.FC = () => {
    const { pathname } = useLocation();
    const history = useHistory();
    const basePath = `${ROUTES.BUNDLE_PRICING.INDEX}${ROUTES.BUNDLE_PRICING.EDIT}`;

    const { setTopAlertMsg, userInfo, setToasts, metadata } = React.useContext(AppContext);
    const territories: Territories = useMemo(() => Object.values(metadata.territoryMapping), [metadata]);
    const currencies: string[] = useMemo(
        () =>
            dropUndefined(
                Array.from(new Set(territories.flatMap((t) => [t.accrualCurrency, t.settlementCurrency, t.currency])))
            ),
        [territories]
    );

    // Keep copy of existing row data for comparison in review step
    const [existingBundlePricingRowData, setExistingBundlePricingRowData] = useState<IBundlePricingRowData[]>([]);
    const [bundlePricingRowData, setBundlePricingRowData] = useState<IBundlePricingRowData[]>([]);
    const [paperPlanNames, setPaperPlanNames] = useState<string[]>([]);
    const [paperPlans, setPaperPlans] = useState<PlanMetadata>([]);
    const [errors, dispatchErrors] = useReducer(bundlePricingValidationReducer, DEFAULT_BUNDLE_PRICING_ERRORS);
    const [isDataLoaded, setIsDataLoaded] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
    const [isSaveModalOpen, setIsSaveModalOpen] = useState(false);

    const currentStepIndex = useMemo(
        () => ADD_BUNDLE_PRICING_STEPS.findIndex(({ stepId }) => stepId === `/${pathname.split('/').pop()}`),
        [pathname]
    );

    const handlePrevious = () => history.push(`${basePath}${ADD_BUNDLE_PRICING_STEPS[currentStepIndex - 1].stepId}`);
    const handleNext = () => history.push(`${basePath}${ADD_BUNDLE_PRICING_STEPS[currentStepIndex + 1].stepId}`);
    const handleCancel = () => setIsCancelModalOpen(true);
    const handlePublish = () => setIsSaveModalOpen(true);
    const handleSave = () => {
        setIsSubmitting(true);
        const input = convertBundlePricingRowDataToApiPayload(bundlePricingRowData, paperPlans, userInfo.username);
        contractConfigurationClient.setBundlePricing(input).subscribe(
            () => {
                const toast: IToast = {
                    id: 'bundle-pricing-updated-toast',
                    timeout: DEFAULT_TOASTER_TIMEOUT,
                    message: 'Bundle pricing updated',
                    type: 'success'
                };
                setToasts([toast]);
                history.push(ROUTES.BUNDLE_PRICING.INDEX);
            },
            (error: UnknownCoralError) => setTopAlertMsg(error.receivedException.message),
            () => setIsSubmitting(false)
        );
    };

    useEffect(() => {
        contractConfigurationClient
            .getPaperPlanMetadata(
                GetPaperPlanMetadataInput.clone({
                    includeDeleted: false,
                    includeAllAttributeSets: true
                })
            )
            .subscribe(
                (response: GetPaperPlanMetadataOutput) => {
                    const rowData = mapPaperPlanMetadataToBundlePricingRowData(response);
                    setPaperPlans(response.planMetadata ?? []);
                    setPaperPlanNames(response.planMetadata?.map((pm) => pm.paperPlan?.paperPlanName ?? '') ?? []);
                    setBundlePricingRowData(rowData);
                    setExistingBundlePricingRowData(rowData);
                },
                (error: UnknownCoralError) => {
                    setTopAlertMsg(error.receivedException.message);
                },
                () => setIsDataLoaded(true)
            );
    }, []);

    // Validate
    useEffect(() => {
        dispatchErrors({
            rowData: bundlePricingRowData,
            territories,
            currencies,
            paperPlanNames
        });
    }, [bundlePricingRowData]);

    return (
        <Column height="100%" spacingInset="400">
            <BreadcrumbWrapper breadcrumbItems={ADD_BUNDLE_PRICING_BREADCRUMB_ITEMS} />
            {isDataLoaded ? (
                <>
                    <Row height="100%" alignmentVertical="stretch" widths={['fit', 'fill']}>
                        <Column minWidth="220px" alignmentHorizontal="start" spacing="200">
                            <StepTracker
                                steps={ADD_BUNDLE_PRICING_STEPS}
                                trackLength={4}
                                currentStepIndex={currentStepIndex}
                                basePath={basePath}
                            />
                        </Column>
                        <Switch>
                            {ADD_BUNDLE_PRICING_STEPS.map(({ stepId, StepComponent }) => (
                                <Route key={stepId} path={`${basePath}${stepId}`}>
                                    <StepComponent
                                        existingBundlePricingRowData={existingBundlePricingRowData}
                                        bundlePricingRowData={bundlePricingRowData}
                                        setBundlePricingRowData={setBundlePricingRowData}
                                        paperPlanNames={paperPlanNames}
                                        errors={errors}
                                    />
                                </Route>
                            ))}
                        </Switch>
                    </Row>
                    <Row alignmentHorizontal="end" spacing="300">
                        <Button type="link" minWidth="96px" onClick={handleCancel}>
                            Cancel
                        </Button>
                        {currentStepIndex > 0 && (
                            <Button
                                type="tertiary"
                                minWidth="96px"
                                onClick={handlePrevious}
                                disabled={!isEmpty(errors)}
                            >
                                Previous
                            </Button>
                        )}
                        {currentStepIndex < ADD_BUNDLE_PRICING_STEPS.length - 1 && (
                            <Button minWidth="96px" onClick={handleNext} disabled={!isEmpty(errors)}>
                                Next
                            </Button>
                        )}
                        {currentStepIndex === ADD_BUNDLE_PRICING_STEPS.length - 1 && (
                            <Button minWidth="96px" onClick={handlePublish}>
                                Publish
                            </Button>
                        )}
                    </Row>
                </>
            ) : (
                <Row height="100%" alignmentHorizontal="center" alignmentVertical="center">
                    <Loader />
                </Row>
            )}

            <ConfirmationModal
                isOpen={isCancelModalOpen}
                onClose={() => setIsCancelModalOpen(false)}
                title="Cancel edit bundle pricing"
                body="Are you sure you want to cancel? Any changes will be lost."
                primaryButtonText="Yes, cancel"
                primaryButtonOnClick={() => history.push(ROUTES.BUNDLE_PRICING.INDEX)}
                secondaryButtonText="No, go back"
                secondaryButtonOnClick={() => setIsCancelModalOpen(false)}
            />

            <ConfirmationModal
                isOpen={isSaveModalOpen}
                onClose={() => setIsSaveModalOpen(false)}
                title="Save bundle pricing"
                body="Are you sure you want to save updates?"
                showWarning={true}
                warning="Updates may require reruns for past accounting periods."
                primaryButtonText="Yes, save updates"
                primaryButtonOnClick={handleSave}
                secondaryButtonText="No, go back"
                secondaryButtonOnClick={() => setIsSaveModalOpen(false)}
                isLoading={isSubmitting}
            />
        </Column>
    );
};
